import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material'
import {
  ArrowBack,
  Business,
  Description,
  Visibility,
  Edit,
  Search,
  TrendingUp,
  CheckCircle,
  Schedule,
  Create
} from '@mui/icons-material'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'

const GestionView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, canEditGestion } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [gestion, setGestion] = useState(null)
  const [procedimientos, setProcedimientos] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadGestionData()
  }, [id])

  const loadGestionData = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      
      // Cargar información de la gestión
      const gestionResponse = await axios.get(`/api/gestiones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      // Cargar procedimientos de la gestión
      const procedimientosResponse = await axios.get(`/api/procedimientos?gestion_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setGestion(gestionResponse.data)
      // Extraer el array de procedimientos de la respuesta
      const procedimientosData = procedimientosResponse.data?.procedimientos || []
      setProcedimientos(procedimientosData)

      // Debug log
      console.log('Gestión cargada:', gestionResponse.data)
      console.log('Procedimientos cargados:', procedimientosData.length)
    } catch (error) {
      setError('Error al cargar información: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado': return 'success'
      case 'revision': return 'warning'
      case 'borrador': return 'info'
      default: return 'default'
    }
  }

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'aprobado': return <CheckCircle />
      case 'revision': return <Schedule />
      case 'borrador': return <Create />
      default: return <Create />
    }
  }

  const canEdit = () => {
    return canEditGestion(parseInt(id))
  }

  const handleViewProcedimiento = (procedimientoId) => {
    navigate(`/procedimientos/${procedimientoId}`)
  }

  const handleEditProcedimiento = (procedimientoId) => {
    navigate(`/procedimientos/${procedimientoId}/editar`)
  }

  // Filtrar procedimientos
  const filteredProcedimientos = procedimientos.filter(proc =>
    proc.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proc.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proc.proceso.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Estadísticas
  const stats = {
    total: procedimientos.length,
    aprobados: procedimientos.filter(p => p.estado === 'aprobado').length,
    revision: procedimientos.filter(p => p.estado === 'revision').length,
    borradores: procedimientos.filter(p => p.estado === 'borrador').length
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando información de la gestión...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
        >
          Volver al Dashboard
        </Button>
      </Box>
    )
  }

  if (!gestion) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gestión no encontrada
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
        >
          Volver al Dashboard
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: gestion.color || '#64B5F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Business sx={{ fontSize: 32, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1">
              {gestion.nombre}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip
                label={gestion.codigo}
                sx={{
                  backgroundColor: gestion.color || '#64B5F6',
                  color: '#fff',
                  fontWeight: 'bold',
                  border: `2px solid ${gestion.color || '#64B5F6'}`
                }}
              />
              <Chip
                label={gestion.activa ? 'Activa' : 'Inactiva'}
                color={gestion.activa ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
          >
            Volver al Dashboard
          </Button>
          {canEdit() && (
            <Button
              variant="contained"
              startIcon={<Description />}
              onClick={() => navigate('/procedimientos/nuevo')}
            >
              Nuevo Procedimiento
            </Button>
          )}
        </Box>
      </Box>

      {/* Información General */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Información General"
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: `${gestion.color || '#64B5F6'}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Business sx={{ color: gestion.color || '#64B5F6' }} />
                </Box>
              }
            />
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Descripción
              </Typography>
              <Typography variant="body1" paragraph>
                {gestion.descripcion || 'Sin descripción'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Estadísticas */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Estadísticas de Procedimientos"
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: `${gestion.color || '#64B5F6'}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUp sx={{ color: gestion.color || '#64B5F6' }} />
                </Box>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ color: gestion.color || '#64B5F6' }} fontWeight="bold">
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {stats.aprobados}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aprobados
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {stats.revision}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      En Revisión
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {stats.borradores}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Borradores
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Procedimientos */}
      <Card>
        <CardHeader
          title="Procedimientos de la Gestión"
          avatar={
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: `${gestion.color || '#64B5F6'}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Description sx={{ color: gestion.color || '#64B5F6' }} />
            </Box>
          }
          action={
            <TextField
              size="small"
              placeholder="Buscar procedimientos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          }
        />
        <CardContent>
          {filteredProcedimientos.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {searchTerm 
                  ? 'No se encontraron procedimientos con ese criterio de búsqueda' 
                  : 'Esta gestión no tiene procedimientos creados aún'
                }
              </Typography>
              {canEdit() && !searchTerm && (
                <Button
                  variant="contained"
                  startIcon={<Description />}
                  onClick={() => navigate('/procedimientos/nuevo')}
                  sx={{ mt: 2 }}
                >
                  Crear Primer Procedimiento
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Proceso</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Versión</TableCell>
                    <TableCell>Última Actualización</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProcedimientos.map((procedimiento) => (
                    <TableRow 
                      key={procedimiento.id} 
                      hover 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewProcedimiento(procedimiento.id)}
                    >
                      <TableCell>
                        <Chip
                          label={procedimiento.codigo}
                          size="small"
                          sx={{
                            backgroundColor: gestion.color || '#64B5F6',
                            color: '#fff',
                            fontWeight: 'bold',
                            border: `2px solid ${gestion.color || '#64B5F6'}`
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {procedimiento.titulo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {procedimiento.proceso}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getEstadoIcon(procedimiento.estado)}
                          <Chip 
                            label={procedimiento.estado.toUpperCase()} 
                            color={getEstadoColor(procedimiento.estado)} 
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          v{procedimiento.version}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(procedimiento.updated_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Ver Procedimiento">
                            <IconButton
                              size="small"
                              onClick={() => handleViewProcedimiento(procedimiento.id)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {canEdit() && (
                            <Tooltip title="Editar Procedimiento">
                              <IconButton
                                size="small"
                                onClick={() => handleEditProcedimiento(procedimiento.id)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default GestionView